import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import ConfirmAction from "./ConfirmAction";
import { useToolStyles } from "./styles";
import { adminToolsApi, parseId } from "../../utils/adminToolsApi";

const HeaderRow = ({ labels }) => {
  const classes = useToolStyles();
  return (
    <TableHead>
      <TableRow>
        {labels.map((label) => (
          <TableCell key={label} className={classes.headerCell}>
            {label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const EventOrderTool = () => {
  const classes = useToolStyles();
  const [includePast, setIncludePast] = useState(false);
  const [collisions, setCollisions] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [meetIdInput, setMeetIdInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadCollisions = useCallback(async () => {
    setLoadingList(true);
    try {
      setCollisions(
        (await adminToolsApi.eventOrderCollisions(includePast)) || [],
      );
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingList(false);
    }
  }, [includePast]);

  useEffect(() => {
    loadCollisions();
  }, [loadCollisions]);

  const showPreview = async (meetId) => {
    setPreviewing(true);
    try {
      setPreview(await adminToolsApi.previewEventOrder(meetId));
      setMeetIdInput(String(meetId));
    } catch (error) {
      setPreview(null);
      toast.error(error.message);
    } finally {
      setPreviewing(false);
    }
  };

  const repair = async () => {
    setBusy(true);
    try {
      const result = await adminToolsApi.repairEventOrder(preview.meetId);
      toast.success(
        `Meet ${result.meetId}: ${result.changedCount} event(s) renumbered`,
      );
      setPreview(result);
      setConfirmOpen(false);
      loadCollisions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const typedMeetId = parseId(meetIdInput);
  const changedRows = (preview?.events || []).filter((row) => row.changed);

  return (
    <Paper
      className={classes.section}
      component="section"
      aria-labelledby="event-order-tool"
    >
      <Typography
        id="event-order-tool"
        component="h2"
        className={classes.sectionTitle}
      >
        Repair event order
      </Typography>
      <Typography className={classes.sectionText}>
        Renumbers the events in every session of a meet so each one gets its own
        slot, keeping the current running order. Events that wrongly share a
        slot are put in alphabetical order, so check the preview with the meet
        host. Combined and split events keep sharing a slot on purpose. Nothing
        changes until you apply.
      </Typography>

      <Box className={classes.actions}>
        <TextField
          label="Meet ID"
          variant="outlined"
          size="small"
          className={classes.idField}
          value={meetIdInput}
          onChange={(e) => setMeetIdInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && typedMeetId) showPreview(typedMeetId);
          }}
          inputProps={{ inputMode: "numeric" }}
        />
        <Button
          variant="outlined"
          className={classes.touch}
          disabled={!typedMeetId || previewing}
          onClick={() => showPreview(typedMeetId)}
        >
          Preview
        </Button>
        {previewing && <CircularProgress size={20} />}
      </Box>

      {preview && (
        <Box marginBottom="20px">
          <Box className={classes.summary}>
            Meet {preview.meetId}:{" "}
            {preview.applied
              ? `${preview.changedCount} of ${preview.events.length} events were renumbered.`
              : preview.changedCount === 0
              ? `all ${preview.events.length} events are already in order.`
              : `${preview.changedCount} of ${preview.events.length} events would be renumbered.`}
          </Box>
          {changedRows.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label="Events that move">
                <HeaderRow
                  labels={[
                    "Session",
                    "Event",
                    "Event ID",
                    "Slot now",
                    "New slot",
                  ]}
                />
                <TableBody>
                  {changedRows.map((row) => (
                    <TableRow key={row.eventId}>
                      <TableCell className={classes.cell}>
                        {row.sessionId}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {row.eventTitle}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {row.eventId}
                      </TableCell>
                      <TableCell
                        className={`${classes.cell} ${classes.number}`}
                      >
                        {row.oldOrder ?? "—"}
                      </TableCell>
                      <TableCell
                        className={`${classes.cell} ${classes.number}`}
                      >
                        {row.newOrder}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!preview.applied && preview.changedCount > 0 && (
            <Box marginTop="12px">
              <Button
                variant="contained"
                color="primary"
                className={classes.touch}
                onClick={() => setConfirmOpen(true)}
              >
                Apply repair
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Box className={classes.actions}>
        <Typography component="h3" style={{ fontWeight: 700, fontSize: 15 }}>
          Meets with shared slots
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
              color="primary"
            />
          }
          label="Include past meets"
        />
        {loadingList && <CircularProgress size={20} />}
      </Box>
      <TableContainer>
        <Table size="small" aria-label="Meets with shared event slots">
          <HeaderRow
            labels={[
              "Meet",
              "Status",
              "Starts",
              "Sessions",
              "Shared slots",
              "",
            ]}
          />
          <TableBody>
            {!loadingList && collisions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className={classes.cell}>
                  No meets need a repair.
                </TableCell>
              </TableRow>
            )}
            {collisions.map((meet) => (
              <TableRow key={meet.meetId}>
                <TableCell className={classes.cell}>
                  {meet.meetName}{" "}
                  <span className={classes.muted}>#{meet.meetId}</span>
                </TableCell>
                <TableCell className={classes.cell}>{meet.status}</TableCell>
                <TableCell className={classes.cell}>
                  {meet.startDate || "—"}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {meet.sessions}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {meet.sharedSlots}
                </TableCell>
                <TableCell className={classes.cell}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={previewing}
                    onClick={() => showPreview(meet.meetId)}
                  >
                    Preview
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmAction
        open={confirmOpen}
        title="Apply the event order repair?"
        text={`Renumbers ${preview?.changedCount ?? 0} event(s) in meet ${
          preview?.meetId ?? ""
        }. The meet's schedule and start lists will show the new order.`}
        confirmLabel="Apply repair"
        busy={busy}
        onConfirm={repair}
        onCancel={() => setConfirmOpen(false)}
      />
    </Paper>
  );
};

export default EventOrderTool;
